import type * as React from 'react';
import { Accordion as BaseAccordion } from '@base-ui/react/accordion';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../lib/cn.ts';

/*
 * A list of things you open one at a time.
 *
 * Built on the Base UI primitive rather than on a `useState` index, because the
 * keyboard and screen-reader contract is the whole job here: arrow keys move
 * between headers, the panel is owned by its trigger, and the open state is
 * announced. A hand-rolled version gets the visuals right and that contract
 * wrong.
 *
 * Panels stay mounted and are hidden with `hidden="until-found"` semantics, so
 * in-page find still reaches a closed answer.
 */

export type AccordionProps = Omit<BaseAccordion.Root.Props, 'className' | 'style'> & {
  className?: string;
};

export function Accordion({ className, ...props }: AccordionProps) {
  return (
    <BaseAccordion.Root
      data-slot='accordion'
      className={cn('flex w-full flex-col gap-3', className)}
      {...props}
    />
  );
}

export type AccordionItemProps = Omit<BaseAccordion.Item.Props, 'className' | 'style'> & {
  className?: string;
  ref?: React.Ref<HTMLDivElement>;
  /*
   * Where the item sits while it is being dragged, and how it travels once it
   * is let go. Both come from the drag library the page uses, which is why
   * they are passed in rather than written here.
   */
  dragTransform?: string;
  dragTransition?: string;
  /* True for the one item being carried, so it lifts off the ones it crosses. */
  dragging?: boolean;
};

export function AccordionItem({
  className,
  dragTransform,
  dragTransition,
  dragging,
  ...props
}: AccordionItemProps) {
  return (
    <BaseAccordion.Item
      data-slot='accordion-item'
      style={{ transform: dragTransform, transition: dragTransition }}
      className={cn(
        'overflow-hidden rounded-lg border border-line bg-surface',
        dragging && 'relative z-10 shadow-raise',
        className,
      )}
      {...props}
    />
  );
}

export interface AccordionTriggerProps extends Omit<
  BaseAccordion.Trigger.Props,
  'className' | 'style'
> {
  className?: string;
  children: React.ReactNode;
  /**
   * Whether the trigger draws its own chevron. Turn it off where the row it
   * sits in ends in something else — a row whose action button comes after the
   * trigger has to place the chevron itself, or the mark that says "this opens"
   * lands in the middle of the row instead of at the end of it.
   */
  chevron?: boolean;
}

export function AccordionTrigger({
  className,
  children,
  chevron = true,
  ...props
}: AccordionTriggerProps) {
  return (
    <BaseAccordion.Header data-slot='accordion-header' className='flex'>
      <BaseAccordion.Trigger
        data-slot='accordion-trigger'
        className={cn(
          'group flex w-full cursor-pointer items-center justify-between gap-4 px-5.5 py-4 text-left',
          'text-md font-semibold text-ink transition-colors hover:bg-fill-faint',
          'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ink-faint',
          className,
        )}
        {...props}
      >
        {children}
        {/* Turns with the panel, so the control still reads as open when the answer is long. */}
        {chevron && (
          <ChevronDown
            aria-hidden='true'
            className='size-4 shrink-0 text-ink-muted transition-transform duration-200 group-data-[panel-open]:rotate-180'
          />
        )}
      </BaseAccordion.Trigger>
    </BaseAccordion.Header>
  );
}

export type AccordionPanelProps = Omit<
  BaseAccordion.Panel.Props,
  'className' | 'style'
> & {
  className?: string;
};

export function AccordionPanel({ className, ...props }: AccordionPanelProps) {
  return (
    <BaseAccordion.Panel
      data-slot='accordion-panel'
      /*
       * Closed panels stay in the DOM rather than unmounting, so the browser's
       * own find still reaches a closed answer. On an FAQ that is the difference
       * between finding what you came for and opening seven panels looking.
       */
      keepMounted
      /*
       * The height variable is what makes the open/close animate at all — the
       * panel has no fixed height to transition between, so Base UI measures it
       * and hands it over as `--accordion-panel-height`.
       */
      className={cn(
        'h-[var(--accordion-panel-height)] overflow-hidden transition-[height] duration-200 ease-out',
        'data-[ending-style]:h-0 data-[starting-style]:h-0',
        className,
      )}
      {...props}
    />
  );
}
